import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../context/ToastContext';
import { registerUser } from '../../../redux/userRelated/userHandle';
import { underControl } from '../../../redux/userRelated/userSlice';
import { 
    CircularProgress, Box, Typography, Button, Container, 
    Paper, TextField, IconButton, Divider, Grid, Avatar 
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import styled, { keyframes } from 'styled-components';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const AddTeacher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { status, response, error, currentUser } = useSelector((state) => state.user);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [password, setPassword] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const { showToast } = useToast();
  const [loader, setLoader] = useState(false);

  const role = 'Teacher';
  const school = currentUser?._id;

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
  };

  const getFields = () => {
    const baseFields = { name, email, phone, address, password, role, school };
    return photo ? { ...baseFields, photo } : baseFields;
  };

  const submitHandler = (event) => {
    event.preventDefault();

    if (!school) {
      showToast('School not found. Please login as admin first.', 'error');
      return;
    }

    setLoader(true);
    dispatch(registerUser(getFields(), role));
  };

  useEffect(() => {
    if (status === 'added') {
      setLoader(false);
      dispatch(underControl());
      showToast('Teacher added successfully!', 'success');
      setTimeout(() => navigate('/Admin/teachers'), 1500);
    } else if (status === 'failed') {
      showToast(response || 'Failed to add teacher', 'error');
      setLoader(false);
    } else if (status === 'error') {
      showToast(error || 'Network Error', 'error');
      setLoader(false);
    }
  }, [status, navigate, error, response, dispatch]);

  return (
    <StyledPageContainer>
      <StyledContainer maxWidth="md">
        <StyledPaper elevation={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#f5f5f5' }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                Add New Teacher 👨‍🏫
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Create a new teacher account for your school
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          <form onSubmit={submitHandler}>
            <Grid container spacing={4}>
              {/* Photo Section */}
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  gap: 2 
                }}>
                  <AvatarWrapper>
                    <Avatar
                      src={photoPreview}
                      sx={{ 
                        width: 160, 
                        height: 160, 
                        bgcolor: '#f0f2f5',
                        border: '4px solid white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    >
                      {!photoPreview && <PersonAddIcon sx={{ fontSize: 60, color: '#667eea' }} />}
                    </Avatar>
                    {photoPreview && (
                      <IconButton 
                        onClick={removePhoto}
                        sx={{ 
                          position: 'absolute', 
                          top: 0, 
                          right: 0, 
                          bgcolor: '#ff4d4d', 
                          color: 'white',
                          '&:hover': { bgcolor: '#ff3333' },
                          boxShadow: 2
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </AvatarWrapper>
                  
                  <input
                    id="teacher-photo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    style={{ display: 'none' }}
                  />
                  
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => document.getElementById('teacher-photo-upload').click()}
                    sx={{ 
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Upload Photo
                  </Button>
                  <Typography variant="caption" color="textSecondary" align="center">
                    JPG, PNG, GIF - Max 5MB<br/>(Optional)
                  </Typography>
                </Box>
              </Grid>

              {/* Form Section */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      variant="outlined"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="e.g. John Doe"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      variant="outlined"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="e.g. john@school.com"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      type="tel"
                      variant="outlined"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="e.g. +977 98XXXXXXXX"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      multiline
                      rows={2}
                      variant="outlined"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Teacher's current address"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      variant="outlined"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 4, bgcolor: '#f8f9fa', p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    <strong>Note:</strong> Class and subject assignments can be managed later from the Teachers list.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 5, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{ 
                  borderRadius: '10px', 
                  px: 4, 
                  py: 1.2,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                type="submit" 
                disabled={loader}
                sx={{ 
                  borderRadius: '10px', 
                  px: 6, 
                  py: 1.2,
                  fontWeight: 600,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                  }
                }}
              >
                {loader ? <CircularProgress size={24} color="inherit" /> : 'Register Teacher'}
              </Button>
            </Box>
          </form>
        </StyledPaper>
      </StyledContainer>
    </StyledPageContainer>
  );
};

export default AddTeacher;

const StyledPageContainer = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
  padding-top: 40px;
  padding-bottom: 40px;
`;

const StyledContainer = styled(Container)`
  animation: ${fadeIn} 0.6s ease-out;
`;

const StyledPaper = styled(Paper)`
  padding: 40px;
  border-radius: 20px !important;
  background-color: white !important;
  box-shadow: 0 10px 30px rgba(0,0,0,0.05) !important;
`;

const AvatarWrapper = styled(Box)`
  position: relative;
  margin-bottom: 8px;
`;
